import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { AttachmentLink } from "@/components/TransactionAttachment";
import { RetailExportButtons, soldQuantity } from "@/components/RetailExportButtons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Store as StoreIcon, Filter } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";
import { toast } from "sonner";

export default function StoreSalesHistory() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = storeId && storeId !== "all" ? `/store-sales?store_id=${storeId}` : "/store-sales";
      const [a, b] = await Promise.all([api.get(url), api.get("/stores")]);
      setItems(a.data.map(row => ({ ...row, sold_qty: soldQuantity(row) }))); setStores(b.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [storeId]);

  // Filter tanggal (di sisi client biar cepat)
  const filtered = useMemo(() => {
    return items.filter((s) => {
      const d = (s.date || "").slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }, [items, dateFrom, dateTo]);

  const tf = useTableFilter(filtered, {
    searchKeys: ["store_name", "payment_method", "notes"],
    defaultSort: { key: "date", dir: "desc" },
  });

  // Ringkasan
  const summary = useMemo(() => {
    const total = tf.rows.reduce((s, r) => s + Number(r.total || 0), 0);
    const items = tf.rows.reduce((s, r) => s + r.sold_qty, 0);
    const stores = new Set(tf.rows.map((r) => r.store_id)).size;
    return { total, items, count: tf.rows.length, stores };
  }, [tf.rows]);

  // Per-store aggregation (untuk pemisahan)
  const perStore = useMemo(() => {
    const map = {};
    for (const r of tf.rows) {
      if (!map[r.store_id]) map[r.store_id] = { store_id: r.store_id, store_name: r.store_name, count: 0, qty: 0, total: 0, last_date: "" };
      map[r.store_id].count += 1;
      map[r.store_id].qty += r.sold_qty;
      map[r.store_id].total += Number(r.total || 0);
      if ((r.date || "") > (map[r.store_id].last_date || "")) map[r.store_id].last_date = r.date;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [tf.rows]);

  const storeTF = useTableFilter(perStore, { defaultSort: { key: "total", dir: "desc" } });
  const invalidRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  return (
    <>
      <PageHeader
        title="Riwayat Penjualan Retail"
        subtitle="Lihat semua laporan penjualan retail — bisa filter per toko, sortir, dan ekspor"
        testId="page-store-sales-history"
        actions={
          <RetailExportButtons rows={tf.rows} from={dateFrom} to={dateTo} title="Riwayat Penjualan Retail" storeLabel={storeId === "all" ? "Semua Toko" : stores.find(s => s.id === storeId)?.name} search={tf.search} disabled={loading} testId="history"/>
        }
      />

      {/* Filter Bar */}
      <Card className="p-4 border border-[var(--hotto-border)] rounded-md mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500"/>
          <div className="text-sm font-semibold text-slate-700">Filter Riwayat</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Toko</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger data-testid="filter-store"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Toko</SelectItem>
                {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from"/>
          </div>
          <div>
            <Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to"/>
          </div>
          <div className="flex items-end">
            <Button variant="ghost" onClick={() => { setStoreId("all"); setDateFrom(""); setDateTo(""); }} className="text-xs" data-testid="history-reset-filters">Reset Filter</Button>
          </div>
        </div>
      </Card>

      {invalidRange && <p role="alert" className="text-red-600 text-sm mb-3" data-testid="history-date-error">Tanggal awal tidak boleh melewati tanggal akhir.</p>}
      <p className="text-xs text-slate-500 mb-3" data-testid="history-export-hint">Unduhan mengikuti rentang tanggal, toko, dan pencarian yang dipilih.</p>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Total Transaksi</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-primary)]">{summary.count}</div>
        </Card>
        <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Total Pendapatan</div>
          <div className="font-mono-num text-2xl font-bold text-emerald-600">{formatIDR(summary.total)}</div>
        </Card>
        <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Toko Terlibat</div>
          <div className="font-mono-num text-2xl font-bold text-blue-600">{summary.stores}</div>
        </Card>
        <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Kuantitas Terjual</div>
          <div data-testid="history-total-quantity" className="font-mono-num text-2xl font-bold text-slate-700">{summary.items}</div>
        </Card>
      </div>

      {/* Ringkasan Per Toko */}
      {(storeId === "all") && (
        <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden mb-4">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Ringkasan Per Toko</div>
              <div className="text-xs text-slate-500">Klik nama toko untuk memfilter riwayatnya</div>
            </div>
          </div>
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={storeTF} col="store_name" testId="summary-sort-store">Toko</SortHeader>
              <SortHeader tf={storeTF} col="count" className="text-right" testId="summary-sort-count">Transaksi</SortHeader>
              <SortHeader tf={storeTF} col="qty" className="text-right" testId="summary-sort-quantity">Kuantitas Terjual</SortHeader>
              <SortHeader tf={storeTF} col="total" className="text-right" testId="summary-sort-total">Total Pendapatan</SortHeader>
              <SortHeader tf={storeTF} col="last_date" testId="summary-sort-date">Transaksi Terakhir</SortHeader>
            </TableRow></TableHeader>
            <TableBody>
              {storeTF.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500" data-testid="store-summary-empty">Belum ada penjualan untuk filter yang dipilih.</TableCell></TableRow>}
              {storeTF.rows.map((r, idx) => (
                <TableRow key={r.store_id} data-testid={`store-summary-${r.store_id}`} className="table-row-hover cursor-pointer" onClick={() => setStoreId(r.store_id)}>
                  <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-medium flex items-center gap-2">
                    <StoreIcon className="w-4 h-4 text-[var(--hotto-primary)]"/> {r.store_name || "-"}
                  </TableCell>
                  <TableCell className="text-right font-mono-num">{r.count}</TableCell>
                  <TableCell className="text-right font-mono-num" data-testid={`summary-quantity-${r.store_id}`}>{r.qty}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-emerald-700">{formatIDR(r.total)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.last_date ? formatDateTime(r.last_date) : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Detail Tabel Riwayat */}
      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          <TableToolbar tf={tf} placeholder="Cari toko / catatan / metode..." testId="search-history"/>
        </div>
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="w-10">#</TableHead>
            <SortHeader tf={tf} col="date">Tanggal</SortHeader>
            <SortHeader tf={tf} col="store_name">Toko</SortHeader>
            <TableHead>Item</TableHead>
            <SortHeader tf={tf} col="sold_qty" className="text-right">Kuantitas Terjual</SortHeader>
            <TableHead>Lampiran</TableHead>
            <SortHeader tf={tf} col="payment_method">Metode</SortHeader>
            <SortHeader tf={tf} col="total" className="text-right">Total</SortHeader>
            <TableHead>Catatan</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Memuat...</TableCell></TableRow>}
            {!loading && tf.rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Tidak ada data untuk filter yang dipilih.</TableCell></TableRow>}
            {tf.rows.map((s, idx) => (
              <TableRow key={s.id} className="table-row-hover">
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-mono-num text-sm">{formatDateTime(s.date)}</TableCell>
                <TableCell className="font-medium">{s.store_name}</TableCell>
                <TableCell className="text-xs text-slate-600 max-w-md truncate">
                  {(s.items || []).map((it) => `${it.product_name}×${it.qty}`).join(", ")}
                </TableCell>
                <TableCell className="text-right font-mono-num" data-testid={`history-quantity-${s.id}`}>{s.sold_qty}</TableCell>
                <TableCell><AttachmentLink attachment={s.attachment} testId={`history-attachment-${s.id}`}/></TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-[10px]">{s.payment_method || "cash"}</Badge></TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-emerald-700">{formatIDR(s.total)}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate">{s.notes || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
